import {BrowserHistory} from '@wix/devzai-common-client';
import {
    assertDefined,
    evaluateFunction,
    objectPick,
    urlModifyQueryParamsInEncodedUrl,
    urlParseQueryString
} from '@wix/devzai-utils-common';
import type {DoppeViewerPageInfo} from '../../client-server-common';
import {
    DoppeViewerCrossPagesQueryParams,
    doppeViewerPageInfoCreateHome,
    DoppeViewerPageType
} from '../../client-server-common';
import {DoppeViewerAppClientServiceBase} from "./doppe-viewer-app-client-service-base";

/**
 * Navigation options for router methods
 */
export interface DoppeViewerRouterNavigationOptions {
    /**
     * Replace history instead of push (default: false)
     */
    replace?: boolean;
    
    /**
     * Update URL in browser (default: true)
     */
    updateUrl?: boolean;
}

/**
 * Viewer Router Client Service
 * 
 * Manages navigation and browser history for the viewer application.
 * Uses DoppeViewerPagesRoutesManager for URL resolution and generation.
 * 
 * Features:
 * - Type-safe navigation methods for each page type
 * - Browser history management (back/forward buttons)
 * - URL resolution (convert page info ↔ URL)
 * - Deep linking support
 * 
 * @example
 * ```typescript
 * // Initialize
 * router.initialize({
 *     bymoPageProps: bymoPageProps,
 *     onPageChange: (pageInfo) => {
 *         // Handle page change
 *     },
 *     initialPageInfo: homePageInfo
 * });
 * 
 * // Navigate
 * router.navigateToProductItem('products-page-id', 'my-product-slug');
 * 
 * // Get URL
 * const url = router.getPageUrl(productItemPageInfo);
 * ```
 */
export class DoppeViewerRouterClientService extends DoppeViewerAppClientServiceBase {

    private onPageChangeCallbacks: Array<(pageInfo: DoppeViewerPageInfo) => void> = [];
    private currentPageInfo?: DoppeViewerPageInfo;
    private browserHistoryUnlisten?: () => void;
    private isPreviewMode: boolean = false;
    
    /**
     * Access routes manager through client services
     * Follows the dashboard pattern for separation of concerns
     */
    private get routesManager() {
        return this.clientServices.viewerPagesRoutesManager;
    }
    
    // ========================================================================
    // Initialization & Cleanup
    // ========================================================================
    
    /**
     * Initialize the router service
     * 
     * @param options - Initialization options
     * @param options.bymoPageProps - Page props for route resolution
     * @param options.onPageChange - Callback when page changes
     * @param options.initialPageInfo - Initial page info (optional)
     * @param options.isPreviewMode - Whether this router is for a preview (skips BrowserHistory operations)
     */
    public initialize(options: {
        onPageChange?: (pageInfo: DoppeViewerPageInfo) => void;
        initialPageInfo?: DoppeViewerPageInfo;
        isPreviewMode?: boolean;
    }): void {
        const {onPageChange: onPageChangeValue, initialPageInfo: initialPageInfoValue, isPreviewMode = false } = options;


        this.isPreviewMode = isPreviewMode;
        
        if (onPageChangeValue) {
            this.onPageChangeCallbacks.push(onPageChangeValue);
        }
        
        // If no initial page info provided, resolve from current URL
        // Now that bymoPageProps is set, this will work correctly for all page types
        this.currentPageInfo = initialPageInfoValue ?? doppeViewerPageInfoCreateHome();
    }



    public initializeBrowserHistory () {
        // Listen to browser back/forward navigation using BrowserHistory
        this.browserHistoryUnlisten = BrowserHistory.listen(() => {
            const pageInfo = this.resolvePageInfoFromUrl(window.location.href);

            // Update current page without modifying history
            this.currentPageInfo = pageInfo;

            // Notify all listeners
            for (const callback of this.onPageChangeCallbacks) {
                callback(pageInfo);
            }
        });
    }

    /**
     * Dispose the router service (cleanup)
     */
    public dispose(): void {
        if (this.browserHistoryUnlisten) {
            this.browserHistoryUnlisten();
            this.browserHistoryUnlisten = undefined;
        }
    }
    
    // ========================================================================
    // Navigation Methods (Type-Safe for Each Page Type)
    // ========================================================================
    
    /**
     * Navigate to home page
     * @param options - Navigation options
     */
    public navigateToHome(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo = doppeViewerPageInfoCreateHome();
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to content page
     * @param slug - URL slug
     * @param options - Navigation options
     */
    public navigateToContentPage(slug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ContentPage = {
            pageType: DoppeViewerPageType.ContentPage,
            slug: slug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to products list page
     * Uses constant path: /products
     * @param options - Navigation options
     */
    public navigateToProductsList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ProductsList = {
            pageType: DoppeViewerPageType.ProductsList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to product item page
     * Uses constant path: /product/:slug
     * @param productSlug - Product slug
     * @param options - Navigation options
     */
    public navigateToProductItem(productSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ProductItem = {
            pageType: DoppeViewerPageType.ProductItem,
            productSlug: productSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to digital product item page
     * Uses constant path: /digital-product/:slug
     * @param productSlug - Product slug
     * @param options - Navigation options
     */
    public navigateToDigitalProductItem(productSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.DigitalProductItem = {
            pageType: DoppeViewerPageType.DigitalProductItem,
            productSlug: productSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to blog posts list page
     * Uses constant path: /blog
     * @param options - Navigation options
     */
    public navigateToBlogPostsList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.BlogPostsList = {
            pageType: DoppeViewerPageType.BlogPostsList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to blog post item page
     * Uses constant path: /post/:slug
     * @param postSlug - Blog post slug
     * @param options - Navigation options
     */
    public navigateToBlogPostItem(postSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.BlogPostItem = {
            pageType: DoppeViewerPageType.BlogPostItem,
            postSlug: postSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to events list page
     * Uses constant path: /events
     * @param options - Navigation options
     */
    public navigateToEventsList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.EventsList = {
            pageType: DoppeViewerPageType.EventsList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to event item page (RSVP events)
     * Uses constant path: /rsvp-event/:slug
     * @param eventSlug - Event slug
     * @param options - Navigation options
     */
    public navigateToEventItem(eventSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.EventItem = {
            pageType: DoppeViewerPageType.EventItem,
            eventSlug: eventSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to ticketing event item page
     * Uses constant path: /ticket-event/:slug
     * @param eventSlug - Event slug
     * @param options - Navigation options
     */
    public navigateToTicketingEventItem(eventSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.TicketingEventItem = {
            pageType: DoppeViewerPageType.TicketingEventItem,
            eventSlug: eventSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to services list page
     * Uses constant path: /services
     * @param options - Navigation options
     */
    public navigateToServicesList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ServicesList = {
            pageType: DoppeViewerPageType.ServicesList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to service item page
     * Uses constant path: /service/:slug
     * @param serviceSlug - Service slug
     * @param options - Navigation options
     */
    public navigateToServiceItem(serviceSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.AppointmentItem = {
            pageType: DoppeViewerPageType.AppointmentItem,
            serviceSlug: serviceSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }

    /**
     * Navigate to course item page
     * Uses constant path: /course/:slug
     * @param serviceSlug - Course service slug
     * @param options - Navigation options
     */
    public navigateToCourseItem(serviceSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.CourseItem = {
            pageType: DoppeViewerPageType.CourseItem,
            serviceSlug: serviceSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }

    /**
     * Navigate to appointment item page
     * Uses constant path: /appointment/:slug
     * @param serviceSlug - Appointment service slug
     * @param options - Navigation options
     */
    public navigateToAppointmentItem(serviceSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.AppointmentItem = {
            pageType: DoppeViewerPageType.AppointmentItem,
            serviceSlug: serviceSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }

    /**
     * Navigate to class item page
     * Uses constant path: /class/:slug
     * @param serviceSlug - Class service slug
     * @param options - Navigation options
     */
    public navigateToClassItem(serviceSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ClassItem = {
            pageType: DoppeViewerPageType.ClassItem,
            serviceSlug: serviceSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }

    /**
     * Resolve page info from URL
     * 
     * This method combines routes manager parsing with bymoPageProps lookup
     * to determine the actual page type, since multiple routes share the same
     * URL pattern but differ in their semantic meaning.
     * 
     * @param url - URL to parse (optional, defaults to current URL)
     * @returns Page info or home page info if parsing fails
     */
    public resolvePageInfoFromUrl(url?: string): DoppeViewerPageInfo {
        if (!url) {
            return doppeViewerPageInfoCreateHome();
        }

        const parsedPath = this.parseUrlToPath(url);
        
        // If no path or just '/', return home
        if (!parsedPath || parsedPath === '/') {
            return doppeViewerPageInfoCreateHome();
        }
        
        // Use routes manager to parse the path structure
        // This gives us the slug and itemSlug, but not the actual page type
        const routeInfo = this.routesManager.resolveRouteInfo(parsedPath, {});
        
        if (!routeInfo) {
            return doppeViewerPageInfoCreateHome();
        }

        // For all other page types (vertical pages), return routeInfo as-is
        return routeInfo;
    }
    
    /**
     * Get current page info, asserting it exists
     * @returns Current page info (throws if not initialized)
     */
    public getCurrentPageInfo(): DoppeViewerPageInfo {
        return assertDefined(this.currentPageInfo, `DoppeViewerRouterClientService is not initialized.`);
    }

    /**
     * Subscribe to page change events
     * @param callback - Function to call when page changes
     * @returns Unsubscribe function
     */
    public subscribe(callback: (pageInfo: DoppeViewerPageInfo) => void): () => void {
        this.onPageChangeCallbacks.push(callback);
        
        // Return unsubscribe function
        return () => {
            const index = this.onPageChangeCallbacks.indexOf(callback);
            if (index !== -1) {
                this.onPageChangeCallbacks.splice(index, 1);
            }
        };
    }
    
    /**
     * Compare two page info objects for equality
     * Used to prevent redundant navigation when the page info hasn't changed
     * @param pageInfo1 - First page info to compare
     * @param pageInfo2 - Second page info to compare
     * @returns true if both page infos represent the same page
     */
    public doppeViewerPageInfoEquals(
        pageInfo1: DoppeViewerPageInfo | null | undefined,
        pageInfo2: DoppeViewerPageInfo | null | undefined
    ): boolean {
        // Handle null/undefined cases
        if (pageInfo1 === pageInfo2) {
            return true;
        }
        if (!pageInfo1 || !pageInfo2) {
            return false;
        }
        
        // Different page types are not equal
        if (pageInfo1.pageType !== pageInfo2.pageType) {
            return false;
        }
        
        // Compare based on page type
        switch (pageInfo1.pageType) {
            case DoppeViewerPageType.Home:
                // Home pages are always equal if both are home
                return true;
                
            case DoppeViewerPageType.ContentPage:
                // Content pages: compare slug only
                return pageInfo1.slug === (pageInfo2 as DoppeViewerPageInfo.ContentPage).slug;
                
            case DoppeViewerPageType.ProductsList:
            case DoppeViewerPageType.BlogPostsList:
            case DoppeViewerPageType.EventsList:
            case DoppeViewerPageType.ServicesList:
                // List pages are always equal if both are the same list type
                return true;
                
            case DoppeViewerPageType.ProductItem:
                // Product item: compare productSlug
                return pageInfo1.productSlug === (pageInfo2 as DoppeViewerPageInfo.ProductItem).productSlug;
                
            case DoppeViewerPageType.DigitalProductItem:
                // Digital product item: compare productSlug
                return pageInfo1.productSlug === (pageInfo2 as DoppeViewerPageInfo.DigitalProductItem).productSlug;
                
            case DoppeViewerPageType.BlogPostItem:
                // Blog post item: compare postSlug
                return pageInfo1.postSlug === (pageInfo2 as DoppeViewerPageInfo.BlogPostItem).postSlug;
                
            case DoppeViewerPageType.EventItem:
                // Event item: compare eventSlug
                return pageInfo1.eventSlug === (pageInfo2 as DoppeViewerPageInfo.EventItem).eventSlug;
                
            case DoppeViewerPageType.TicketingEventItem:
                // Ticketing event item: compare eventSlug
                return pageInfo1.eventSlug === (pageInfo2 as DoppeViewerPageInfo.TicketingEventItem).eventSlug;
                
            case DoppeViewerPageType.ServiceItem:
                // Service item: compare serviceSlug
                return pageInfo1.serviceSlug === (pageInfo2 as DoppeViewerPageInfo.ServiceItem).serviceSlug;
                
            default:
                // Unknown page type, consider not equal
                return false;
        }
    }
    
    // ========================================================================
    // Private Methods
    // ========================================================================
    
    /**
     * Navigate to a page info object
     * @param pageInfo - Page info to navigate to
     * @param options - Navigation options
     */
    public navigateToPageInfo(pageInfo: DoppeViewerPageInfo, options?: DoppeViewerRouterNavigationOptions): void {
        const { replace: replaceValue, updateUrl: updateUrlValue } = options ?? {};
        
        const replace = replaceValue ?? false;
        const updateUrl = updateUrlValue ?? true;
        
        // Update current page info
        this.currentPageInfo = pageInfo;
        
        // Update URL if requested
        if (updateUrl && !this.isPreviewMode) {
            const sourcePageQueryString = location.search;

            const path = urlModifyQueryParamsInEncodedUrl(
                this.routesManager.resolvePath(pageInfo),
                queryParams => ({
                    ...objectPick(urlParseQueryString(sourcePageQueryString), [...DoppeViewerCrossPagesQueryParams]),
                    ...queryParams,
                })
            );

            if (replace) {
                BrowserHistory.replace(path);
            } else {
                BrowserHistory.push(path);
            }
        }
        else {
            for (const callback of this.onPageChangeCallbacks) {
                callback(pageInfo);
            }
        }
    }
    
    /**
     * Parse URL to path (extract hash or pathname)
     * @param url - Full URL
     * @returns Path string (e.g., '/product/slug')
     * 
     * For hash mode: extracts the hash part (e.g., '#/product/slug' -> '/product/slug')
     * For path mode: returns the pathname (e.g., '/product/slug')
     * 
     * Routes are always root-relative for viewer (no basePath)
     */
    private parseUrlToPath(url: string): string {
        const urlObject = evaluateFunction(() => {
            try {
                return new URL(url);
            } catch {
                return null;
            }
        });
        
        if (!urlObject) {
            return '/';
        }

        return urlObject.pathname;
    }

}

